import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import * as api from '../../../services/assistantClient';
import { AssistantAccount, AssistantError } from '../../../services/assistantClient';
import ProposalCard from './ProposalCard';
import AssistantAccountView from './AssistantAccount';
import { stepHelp, HelpState, HelpAction, HELP_STATE } from '../../../help/helpWorkflow';
import { pointAt, clearPointer } from '../../../help/pointer';
import { HELP_ASK_EVENT, takePendingAsk, AssistantAsk } from '../../../help/helpBus';
import { helpContent } from '../../../help/content';
import { runClientTool } from './clientTools';
import { isEnter } from '../../../utils/keyboard';
import { formatShortDateTime } from '../../../utils/formatters';

/** 고른 제공자·모델은 이 브라우저에 남긴다 — 계정 연결과 새 대화가 같은 것을 쓰도록 */
const PROVIDER_KEY = 'openflow.assistant.provider';
const MODEL_KEY = 'openflow.assistant.model';
const readPref = (key: string, fallback: string) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};
const writePref = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* 사생활 보호 모드 등 — 이번 세션만 쓴다 */ }
};

/**
 * AI 어시스턴트 패널 — 오른쪽 패널의 "AI" 탭.
 *
 * 서비스(qbase/assistant-service)가 대화·도구 호출을 맡고 여기서는 스트림을 그린다.
 * 어시스턴트는 **제안만** 한다: 캔버스 반영도 배포·실행도 사용자가 버튼으로 한다.
 *
 * 도움말 서브에이전트: "도움말/사용법/quick start…" 는 LLM 보다 먼저 프론트 상태기계(help/helpWorkflow)가 받아
 * 메뉴·단계를 **버튼**으로 낸다(options). 「대신 해줘」는 미리 정한 클라이언트 도구를 그대로 실행한다.
 * LLM 이 부르는 클라이언트 도구(get_usage_guide·open_sample_workflow·focus_node·suggest_actions)는
 * SSE `client_tool` 로 와서 clientTools.runClientTool 이 실행하고 결과를 서비스에 돌려준다.
 */

type Item =
  | { k: 'user'; id: string; text: string }
  | { k: 'text'; id: string; text: string; options?: string[] }
  | { k: 'tool'; id: string; name: string; input?: any; result?: string; ok?: boolean; progress?: string }
  | { k: 'question'; id: string; questionId: string; question: string; header?: string; options?: { label: string; description?: string }[]; answer?: string }
  | { k: 'proposal'; id: string; artifact: any; rationale?: string }
  | { k: 'error'; id: string; message: string };

let seq = 0;
const nextId = () => `it_${++seq}`;

/** 저장된 메시지(대화를 다시 열었을 때) → 화면 항목 */
function itemsFromMessages(messages: api.AssistantMessage[]): Item[] {
  const out: Item[] = [];
  for (const m of messages) {
    const p = m.payload || {};
    if (m.kind === 'text') out.push(m.role === 'user' ? { k: 'user', id: m.id, text: m.content } : { k: 'text', id: m.id, text: m.content });
    else if (m.kind === 'tool_call') out.push({ k: 'tool', id: m.id, name: m.content, input: p.input, result: p.result, ok: !p.isError });
    else if (m.kind === 'question') out.push({ k: 'question', id: m.id, questionId: p.questionId, question: m.content, header: p.header, options: p.options, answer: p.resolved?.answer });
    else if (m.kind === 'proposal') out.push({ k: 'proposal', id: m.id, artifact: m.content, rationale: p.rationale });
    else if (m.kind === 'error') out.push({ k: 'error', id: m.id, message: m.content });
    // answer 는 question 항목 안에 표시하므로 따로 만들지 않는다
  }
  return out;
}

const AssistantPanel: React.FC = () => {
  const { t } = useTranslation();
  const workflow = useSelector((s: RootState) => s.workflow.current);
  const nodes = useSelector((s: RootState) => s.workflow.nodes);
  const edges = useSelector((s: RootState) => s.workflow.edges);
  const selectedNodeId = useSelector((s: RootState) => s.workflow.selectedNodeId);

  const [account, setAccount] = useState<AssistantAccount | null>(null);
  // 계정 확인 + 지난 대화 복원이 끝났는가 — 그 전에 메시지를 넣으면 복원이 덮어쓴다 (도움말 대기 요청이 그랬다)
  const [ready, setReady] = useState(false);
  const [accountError, setAccountError] = useState<string>('');
  const [provider, setProvider] = useState<string>(() => readPref(PROVIDER_KEY, 'claude'));
  const [model, setModel] = useState<string>(() => readPref(MODEL_KEY, 'default'));
  const [accountOpen, setAccountOpen] = useState(false);
  const [threads, setThreads] = useState<api.AssistantThread[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [streaming, setStreaming] = useState('');
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState('');
  const [freeAnswer, setFreeAnswer] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  // 도움말 상태기계 — 렌더와 무관하므로 ref. 새 대화를 열면 비운다
  const helpRef = useRef<HelpState | null>(null);
  // LLM 이 suggest_actions 를 부르면 여기 담아 두었다가 턴이 끝날 때 마지막 답변에 버튼으로 붙인다
  const suggestRef = useRef<string[] | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  // 캔버스 최신값을 스트림 콜백에서 읽기 위한 참조(스테일 클로저 방지)
  const canvasRef = useRef({ nodes, edges, workflow, selectedNodeId });
  canvasRef.current = { nodes, edges, workflow, selectedNodeId };

  useEffect(() => {
    let alive = true;
    api.getAccount(provider)
      .then(async (acc) => {
        if (!alive) return;
        setAccount(acc);
        // 가장 최근 대화를 이어서 연다 — 탭을 옮기거나 새로고침해도 흐름이 끊기지 않는다
        try {
          const list = await api.listThreads();
          if (!alive) return;
          setThreads(list);
          if (!list.length) return;
          const { thread, messages } = await api.getThread(list[0].id);
          if (!alive) return;
          setThreadId(thread.id);
          setItems(itemsFromMessages(messages));
        } catch { /* 대화 복원 실패는 새 대화로 시작하면 그만이다 */ }
      })
      .catch((e: any) => { if (alive) setAccountError(e?.message || i18n.t('assistant:panel.serviceFailed')); })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [provider]);

  const changeProvider = useCallback((p: string) => { setProvider(p); writePref(PROVIDER_KEY, p); }, []);
  const changeModel = useCallback((m: string) => { setModel(m); writePref(MODEL_KEY, m); }, []);

  /** 대화 목록 열기 — 지금 목록을 다시 읽는다(다른 탭에서 만든 대화도 보이도록) */
  const openList = useCallback(async () => {
    setListOpen((v) => !v);
    try { setThreads(await api.listThreads()); } catch { /* 목록을 못 읽어도 현재 대화는 그대로다 */ }
  }, []);

  const openThread = useCallback(async (id: string) => {
    if (busy) return;
    setListOpen(false);
    try {
      const { thread, messages } = await api.getThread(id);
      setThreadId(thread.id);
      setItems(itemsFromMessages(messages));
      setStreaming('');
    } catch (e: any) {
      push({ k: 'error', id: nextId(), message: e?.message || i18n.t('assistant:panel.openFailed') });
    }
  }, [busy]);

  const removeThread = useCallback(async (id: string) => {
    if (!window.confirm(i18n.t('assistant:panel.deleteConfirm'))) return;
    try { await api.deleteThread(id); } catch { /* 이미 지워졌을 수 있다 */ }
    setThreads((prev) => prev.filter((t) => t.id !== id));
    if (id === threadId) { setThreadId(null); setItems([]); }
  }, [threadId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [items, streaming]);

  /** 지금 캔버스를 요약해 넘긴다 — 전체 JSON 은 크고, 어시스턴트는 도구로 다시 확인할 수 있다 */
  const buildContext = useCallback(() => {
    const c = canvasRef.current;
    return {
      workflowId: c.workflow?.id || null,
      workflowName: c.workflow?.name || null,
      selectedNodeId: c.selectedNodeId || null,
      canvas: {
        nodes: c.nodes.map((n: any) => ({ id: n.id, type: n.type, label: n.data?.label, config: n.data?.config })),
        edges: c.edges.map((e: any) => ({ id: e.id, source: e.source, target: e.target })),
      },
    };
  }, []);

  const push = (it: Item) => setItems((prev) => [...prev, it]);

  /** 도움말 단계의 동작(샘플 열기·Quick Start 「대신 해줘」) — LLM 을 거치지 않는 미리 정한 도구 호출 */
  const runHelpActions = useCallback(async (actions: HelpAction[]) => {
    for (const a of actions) {
      const id = nextId();
      push({ k: 'tool', id, name: a.name, input: a.input });
      const setLine = (patch: Partial<Extract<Item, { k: 'tool' }>>) =>
        setItems((prev) => prev.map((it) => (it.k === 'tool' && it.id === id ? { ...it, ...patch } : it)));
      const r = await runClientTool({ name: a.name, input: a.input }, {
        // 실행처럼 오래 걸리는 도구는 진행 글을 도구 줄 아래에 그대로 보여 준다
        onProgress: (text) => setLine({ progress: text }),
      });
      setLine({ result: JSON.stringify(r).slice(0, 400), ok: r.ok, progress: undefined });
      // 도구가 사람에게 할 말(summary)을 돌려주면 답변처럼 보여 준다 — 저장 버전, DAG id, 실행 결과, 행 수
      if (typeof (r as any).summary === 'string') push({ k: 'text', id: nextId(), text: (r as any).summary });
      if (!r.ok) {
        push({ k: 'error', id: nextId(), message: i18n.t('assistant:panel.actionFailed', { name: a.name, error: String((r as any).error || '') }) });
        return false;
      }
    }
    return true;
  }, []);

  const send = useCallback(async (text: string, opts: { rewrite?: string } = {}) => {
    if (!text.trim() || busy) return;
    setInput('');

    // ── 도움말 서브에이전트가 먼저 본다 — 메뉴·주제·Quick Start 는 LLM 없이 즉시, agentText 만 LLM 으로 ──
    let toLlm = opts.rewrite || text;
    if (!opts.rewrite) {
      const h = stepHelp(helpRef.current, text);
      if (h) {
        helpRef.current = h.state;
        push({ k: 'user', id: nextId(), text });
        // 동작(도구 호출)이 있으면 먼저 돌리고 안내 글은 그 뒤에 — "결과는 위 줄" 식의 순서가 자연스럽고,
        // 실패해도 안내 글과 [다음]/[그만] 버튼은 나와야 사용자가 이어갈 수 있다
        let ok = true;
        if (h.actions?.length) {
          setBusy(true);
          try { ok = await runHelpActions(h.actions); }
          finally { setBusy(false); }
        }
        h.messages.forEach((m) => push({ k: 'text', id: nextId(), text: m.text, options: m.options }));
        // 단계가 가리키는 곳을 손가락으로 — 사용자가 그곳을 누르거나 다음 단계로 가면 물러난다. Quick Start 를 벗어나면 지운다
        if (h.point) pointAt(h.point.target, { label: h.point.label, hold: 0 });
        else if (h.point === null || h.state?.type !== HELP_STATE.QUICK_START) clearPointer();
        if (!ok || !h.agentText) return;
        toLlm = h.agentText;
      }
    }

    setBusy(true);
    setStreaming('');
    suggestRef.current = null;
    let id = threadId;
    try {
      if (!id) {
        const t = await api.createThread({ provider, model, context: buildContext() });
        id = t.id;
        setThreadId(id);
        setThreads((prev) => [t, ...prev]);
      }
      // 도움말이 이미 사용자 메시지를 그렸으면(LLM 본문만 바꾼 경우) 다시 그리지 않는다
      if (toLlm === text) push({ k: 'user', id: nextId(), text });
      const ac = new AbortController();
      abortRef.current = ac;
      const turnThread = id;
      await api.runTurn(id, toLlm, {
        context: buildContext(),
        signal: ac.signal,
        onEvent: (ev) => {
          const d = ev.data || {};
          switch (ev.type) {
            case 'text_delta':
              setStreaming((s) => s + (d.text || ''));
              break;
            case 'text':
              setStreaming('');
              push({ k: 'text', id: d.messageId || nextId(), text: d.text || '' });
              break;
            case 'tool_call':
              push({ k: 'tool', id: d.id || nextId(), name: d.name || i18n.t('assistant:panel.tool'), input: d.input });
              break;
            case 'tool_result':
              setItems((prev) => prev.map((it) => (it.k === 'tool' && it.id === d.id ? { ...it, result: d.preview, ok: d.ok } : it)));
              break;
            case 'question':
              push({ k: 'question', id: d.messageId || nextId(), questionId: d.questionId, question: d.question, header: d.header, options: d.options });
              break;
            case 'question_resolved':
              setItems((prev) => prev.map((it) => (it.k === 'question' && it.questionId === d.questionId
                ? { ...it, answer: d.source === 'timeout' ? i18n.t('assistant:panel.noResponse') : String(d.answer) } : it)));
              break;
            case 'proposal':
              push({ k: 'proposal', id: d.messageId || nextId(), artifact: d.artifact, rationale: d.rationale });
              break;
            case 'client_tool': {
              // 브라우저가 실행하는 도구 — 화면에서 돌리고 결과를 서비스에 돌려준다 (브로커가 그 결과로 LLM 턴을 잇는다).
              // 화면의 도구 줄은 하네스가 따로 흘리는 tool_call/tool_result 가 맡는다(id 가 다르다) — 여기서 또 그리면 두 줄이 된다(실측)
              const callId = String(d.callId || '');
              runClientTool({ name: d.name, input: d.input || {} }, { onSuggest: (o) => { suggestRef.current = o; } }, true)
                .then(async (r) => {
                  try { await api.submitToolResult(turnThread, callId, r); }
                  catch (err: any) { push({ k: 'error', id: nextId(), message: i18n.t('assistant:panel.toolReturnFailed', { message: err?.message || '' }) }); }
                });
              break;
            }
            case 'error':
              push({ k: 'error', id: nextId(), message: d.message || i18n.t('assistant:panel.error') });
              break;
            default:
              break;
          }
        },
      });
      // suggest_actions — 답변 끝에 "다음 행동" 버튼. 마지막 답변에 붙이고, 답변이 없으면 버튼만 한 줄로
      const sug = suggestRef.current as string[] | null;   // 스트림 콜백이 채운다 — TS 는 흐름을 못 본다
      if (sug && sug.length) {
        suggestRef.current = null;
        setItems((prev) => {
          const back = [...prev].reverse().findIndex((it) => it.k === 'text');
          if (back < 0) return [...prev, { k: 'text', id: nextId(), text: i18n.t('assistant:panel.next'), options: sug }];
          const idx = prev.length - 1 - back;
          return prev.map((it, j) => (j === idx && it.k === 'text' ? { ...it, options: sug } : it));
        });
      }
    } catch (e: any) {
      const msg = e?.name === 'AbortError' ? i18n.t('assistant:panel.aborted') : (e?.message || i18n.t('assistant:panel.requestFailed'));
      push({ k: 'error', id: nextId(), message: msg });
      if (e instanceof AssistantError && e.status === 401) setAccount(null);
    } finally {
      setStreaming('');
      setBusy(false);
      abortRef.current = null;
    }
  }, [busy, threadId, buildContext, provider, model, runHelpActions]);

  // 도움말 메뉴 → 「AI 에게 물어보기」: 입력란에 채우거나(기본) 바로 보낸다. 패널이 늦게 마운트돼도 대기 요청을 가져간다
  const sendRef = useRef(send);
  sendRef.current = send;
  useEffect(() => {
    if (!ready || !account?.loggedIn) return;
    const apply = (ask: AssistantAsk | null) => {
      if (!ask) return;
      if (ask.send) { sendRef.current(ask.text); return; }
      setInput(ask.text);
      setTimeout(() => inputRef.current?.focus(), 50);
    };
    apply(takePendingAsk());
    const onAsk = (e: Event) => { takePendingAsk(); apply((e as CustomEvent<AssistantAsk>).detail); };
    window.addEventListener(HELP_ASK_EVENT, onAsk);
    return () => window.removeEventListener(HELP_ASK_EVENT, onAsk);
  }, [ready, account]);

  const answer = async (it: Extract<Item, { k: 'question' }>, value: string) => {
    if (!threadId || !value.trim()) return;
    setFreeAnswer('');
    try { await api.answerQuestion(threadId, it.questionId, value); }
    catch (e: any) { push({ k: 'error', id: nextId(), message: e?.message || i18n.t('assistant:panel.answerFailed') }); }
  };

  const stop = async () => {
    if (threadId) { try { await api.abortTurn(threadId); } catch { /* 이미 끝났을 수 있다 */ } }
    abortRef.current?.abort();
  };

  const newThread = () => {
    if (busy) return;
    setThreadId(null);
    setItems([]);
    setStreaming('');
    helpRef.current = null;
  };

  // ---- 준비 안 된 상태들 ----
  if (accountError) {
    return (
      <div className="p-3 text-[0.75rem] text-gray-600">
        <div className="mb-1 font-medium text-gray-700">{t('assistant:panel.unavailable')}</div>
        <div className="text-[0.6875rem] text-red-600">{accountError}</div>
        <div className="mt-2 text-[0.6875rem] text-gray-500">
          {t('assistant:panel.unavailableHint')}
        </div>
      </div>
    );
  }
  if (!account) return <div className="p-3 text-[0.75rem] text-gray-500">{t('assistant:panel.preparing')}</div>;
  if (!account.loggedIn || accountOpen) {
    return (
      <AssistantAccountView
        account={account}
        provider={provider}
        onProviderChange={changeProvider}
        model={model}
        onModelChange={changeModel}
        onChanged={setAccount}
        onClose={account.loggedIn ? () => setAccountOpen(false) : undefined}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-2 py-1">
        <button type="button" onClick={() => setAccountOpen(true)}
          className="truncate text-left text-[0.6875rem] text-gray-500 underline decoration-dotted hover:text-gray-700"
          title={t('assistant:panel.accountTitle')}>
          {account.email || account.provider}
          {workflow?.name ? ` · ${workflow.name}` : t('assistant:panel.noWorkflow')}
        </button>
        <div className="ml-2 flex shrink-0 items-center gap-1">
          <button type="button" onClick={openList} disabled={busy}
            className="rounded border border-gray-300 px-2 py-0.5 text-[0.6875rem] text-gray-600 hover:bg-gray-50 disabled:text-gray-300"
            title={t('assistant:panel.historyTitle')}>
            {t('assistant:panel.conversations')}{threads.length ? `(${threads.length})` : ''}
          </button>
          <button type="button" onClick={newThread} disabled={busy}
            className="rounded border border-gray-300 px-2 py-0.5 text-[0.6875rem] text-gray-600 hover:bg-gray-50 disabled:text-gray-300">
            {t('assistant:panel.newChat')}
          </button>
        </div>
      </div>

      {listOpen && (
        <div className="max-h-48 shrink-0 overflow-y-auto border-b border-gray-200 bg-gray-50">
          {threads.length === 0 && <div className="px-2 py-1.5 text-[0.6875rem] text-gray-400">{t('assistant:panel.noHistory')}</div>}
          {threads.map((th) => (
            <div key={th.id} className={`flex items-center gap-1 px-2 py-1 text-[0.6875rem] hover:bg-white ${th.id === threadId ? 'bg-white font-medium' : ''}`}>
              <button type="button" onClick={() => openThread(th.id)} className="flex-1 truncate text-left text-gray-700">
                {th.title || t('assistant:panel.untitled')}
                <span className="ml-1 text-[0.625rem] text-gray-400">
                  {th.updatedAt ? formatShortDateTime(th.updatedAt).slice(0, 11) : ''}
                  {th.busy ? t('assistant:panel.running') : ''}
                </span>
              </button>
              <button type="button" onClick={() => removeThread(th.id)} className="shrink-0 text-gray-400 hover:text-red-500" title={t('assistant:panel.deleteChat')}>✕</button>
            </div>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 space-y-2 overflow-y-auto p-2">
        {items.length === 0 && !streaming && (
          <div className="rounded bg-gray-50 p-2 text-[0.6875rem] leading-relaxed text-gray-500">
            {t('assistant:panel.intro')}
            <div className="mt-1 space-y-0.5">
              <div>{t('assistant:panel.example1')}</div>
              <div>{t('assistant:panel.example2')}</div>
              <div>{t('assistant:panel.example3')}</div>
            </div>
            <div className="mt-1 text-gray-400">{t('assistant:panel.proposeOnly')}</div>
            <button type="button" onClick={() => send(helpContent().triggers.help)}
              className="mt-2 rounded-full border border-blue-300 bg-white px-2.5 py-0.5 text-[0.6875rem] text-blue-700 hover:bg-blue-50">
              {t('assistant:panel.helpButton')}
            </button>
          </div>
        )}

        {items.map((it) => {
          if (it.k === 'user') {
            return (
              <div key={it.id} className="ml-6 whitespace-pre-wrap rounded bg-blue-50 px-2 py-1 text-[0.75rem] text-gray-800">
                {it.text}
              </div>
            );
          }
          if (it.k === 'text') {
            return (
              <div key={it.id}>
                <div className="whitespace-pre-wrap text-[0.75rem] leading-relaxed text-gray-800">{it.text}</div>
                {/* 메시지에 붙은 선택 버튼 — 누르면 그 글이 새 메시지로 간다 (도움말 메뉴·Quick Start·suggest_actions) */}
                {it.options && it.options.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {it.options.map((o) => (
                      <button key={o} type="button" onClick={() => send(o)} disabled={busy}
                        className="rounded-full border border-blue-300 bg-white px-2.5 py-0.5 text-[0.6875rem] text-blue-700 hover:bg-blue-50 disabled:opacity-50">
                        {o}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          if (it.k === 'tool') return <ToolLine key={it.id} item={it} />;
          if (it.k === 'proposal') return <ProposalCard key={it.id} artifact={it.artifact} rationale={it.rationale} />;
          if (it.k === 'error') {
            return (
              <div key={it.id} className="rounded border border-red-200 bg-red-50 px-2 py-1 text-[0.6875rem] text-red-700">
                {it.message}
              </div>
            );
          }
          // question
          return (
            <div key={it.id} className="rounded border border-violet-300 bg-violet-50 p-2">
              {it.header && <div className="text-[0.625rem] font-semibold text-violet-700">{it.header}</div>}
              <div className="whitespace-pre-wrap text-[0.75rem] text-gray-800">{it.question}</div>
              {it.answer ? (
                <div className="mt-1 text-[0.6875rem] text-violet-700">→ {it.answer}</div>
              ) : (
                <div className="mt-1 space-y-1">
                  {(it.options || []).map((o) => (
                    <button key={o.label} type="button" onClick={() => answer(it, o.label)}
                      className="block w-full rounded border border-violet-300 bg-white px-2 py-1 text-left text-[0.6875rem] hover:bg-violet-100">
                      <span className="font-medium">{o.label}</span>
                      {o.description && <span className="ml-1 text-gray-500">{o.description}</span>}
                    </button>
                  ))}
                  <div className="flex gap-1">
                    <input
                      value={freeAnswer}
                      onChange={(e) => setFreeAnswer(e.target.value)}
                      onKeyDown={(e) => { if (isEnter(e)) answer(it, freeAnswer); }}
                      placeholder={t('assistant:panel.typeAnswer')}
                      className="flex-1 rounded border border-violet-300 px-2 py-1 text-[0.6875rem]"
                    />
                    <button type="button" onClick={() => answer(it, freeAnswer)}
                      className="rounded bg-violet-600 px-2 py-1 text-[0.6875rem] text-white hover:bg-violet-700">
                      {t('assistant:panel.send')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {streaming && (
          <div className="whitespace-pre-wrap text-[0.75rem] leading-relaxed text-gray-800">
            {streaming}
            <span className="ml-0.5 animate-pulse text-gray-400">▍</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="shrink-0 border-t border-gray-200 p-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (isEnter(e) && !e.shiftKey) { e.preventDefault(); send(input); }
          }}
          rows={2}
          placeholder={busy ? t('assistant:panel.waiting') : t('assistant:panel.inputPh')}
          disabled={busy}
          className="w-full resize-none rounded border border-gray-300 px-2 py-1 text-[0.75rem] disabled:bg-gray-50"
        />
        <div className="mt-1 flex justify-end gap-1">
          {busy ? (
            <button type="button" onClick={stop}
              className="rounded border border-gray-300 px-2 py-1 text-[0.6875rem] text-gray-600 hover:bg-gray-50">
              {t('assistant:panel.stop')}
            </button>
          ) : (
            <button type="button" onClick={() => send(input)} disabled={!input.trim()}
              className="rounded bg-blue-600 px-3 py-1 text-[0.6875rem] font-medium text-white hover:bg-blue-700 disabled:bg-gray-300">
              {t('assistant:panel.send')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** 도구 호출 한 줄 — 접어 두고, 눌러야 입력·결과를 보여 준다(대화 흐름을 가리지 않도록) */
const ToolLine: React.FC<{ item: Extract<Item, { k: 'tool' }> }> = ({ item }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const done = item.result !== undefined;
  return (
    <div className="rounded bg-gray-50 px-2 py-1 text-[0.6875rem]">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-1 text-left">
        <span>{done ? (item.ok === false ? '⚠' : '🔧') : '⏳'}</span>
        <span className="font-medium text-gray-700">{item.name}</span>
        <span className="ml-auto text-gray-400">{open ? t('assistant:panel.collapse') : t('assistant:panel.details')}</span>
      </button>
      {item.progress && <div className="mt-0.5 truncate text-[0.625rem] text-blue-700" title={item.progress}>{item.progress}</div>}
      {open && (
        <div className="mt-1 space-y-1">
          <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-all rounded bg-white p-1 text-[0.625rem] text-gray-600">
            {JSON.stringify(item.input ?? {}, null, 1)}
          </pre>
          {done && (
            <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-all rounded bg-white p-1 text-[0.625rem] text-gray-600">
              {item.result}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantPanel;
